import { dispatch, select, useDispatch } from '@wordpress/data';
import { PluginBlockSettingsMenuItem } from '@wordpress/edit-post';
import { __ } from '@wordpress/i18n';
import { store as keyboardShortcutsStore, useShortcut } from '@wordpress/keyboard-shortcuts';
import { registerPlugin } from '@wordpress/plugins';

import { useEffect } from '@wordpress/element';
import excludeKeys from './excludes.js';
import SettingsIcons from './icons.js';
import getFaqmateEditorLocalStorage from './local-storage.js';

const faqBCopyPasteStyles = () => {
    // Registering the shortcuts
    const { registerShortcut } = useDispatch(keyboardShortcutsStore);

    const { hasMultiSelection } = select('core/block-editor');
    const faqmateCopyPasteStylesSetter = getFaqmateEditorLocalStorage();

    useEffect(() => {
        const faqmateCopyPasteStyles = getFaqmateEditorLocalStorage('faqmateCopyPasteStyles');

        if (!faqmateCopyPasteStyles) {
            faqmateCopyPasteStylesSetter.setItem('faqmateCopyPasteStyles', JSON.stringify({}));
        }

        if (faqmateCopyPasteStyles) {
            for (const block in faqmateCopyPasteStyles) {
                const hoursSinceStylesSaved = Math.abs(Date.now() - faqmateCopyPasteStyles[block].stylesSavedTimeStamp) / 36e5;

                if (hoursSinceStylesSaved >= 8) {
                    delete faqmateCopyPasteStyles[block];
                }
            }

            faqmateCopyPasteStylesSetter.setItem('faqmateCopyPasteStyles', JSON.stringify(faqmateCopyPasteStyles));
        }

        registerShortcut({
            name: 'faqmate/copy',
            category: 'block',
            description: __('Copy the selected block(s).', 'faqmate-blocks'),
            keyCombination: {
                modifier: 'primaryShift',
                character: 'y'
            }
        });

        registerShortcut({
            name: 'faqmate/paste',
            category: 'block',
            description: __('Paste the selected block(s).', 'faqmate-blocks'),
            keyCombination: {
                modifier: 'primaryShift',
                character: 'u'
            }
        });
    }, []);

    const copyStylesHandler = () => {
        // eslint-disable-next-line no-shadow
        const { getSelectedBlock, hasMultiSelection, getMultiSelectedBlocks } = select('core/block-editor');

        if (hasMultiSelection()) {
            const multiSelectedBlocksData = getMultiSelectedBlocks();
            multiSelectedBlocksData.map(block => {
                if (block) {
                    storeBlockStyles(block);
                }

                return block;
            });

            return;
        }

        const selectedBlockData = getSelectedBlock();

        if (selectedBlockData) {
            storeBlockStyles(selectedBlockData);
        }
    };

    const pasteStylesHandler = () => {
        // eslint-disable-next-line no-shadow
        const { getSelectedBlock, hasMultiSelection, getMultiSelectedBlocks } = select('core/block-editor');

        if (hasMultiSelection()) {
            const multiSelectedBlocksData = getMultiSelectedBlocks();
            multiSelectedBlocksData.map(block => {
                if (block) {
                    pasteBlockStyles(block);
                }

                return block;
            });

            return;
        }

        const selectedBlockData = getSelectedBlock();

        if (selectedBlockData) {
            pasteBlockStyles(selectedBlockData);
        }
    };

    const storeBlockStyles = blockData => {
        const faqmateCopyPasteStyles = getFaqmateEditorLocalStorage('faqmateCopyPasteStyles');
        faqmateCopyPasteStylesSetter.setItem('faqmateCopyPasteStyles', JSON.stringify({}));

        const { attributes, name, innerBlocks } = blockData;

        if (faqmateCopyPasteStyles) {
            faqmateCopyPasteStylesSetter.setItem('faqmateCopyPasteStyles', JSON.stringify({}));
        }

        let styles = {};
        const parentStyle = {};

        if (name.includes('faqmate/')) {
            const blockName = name.replace('faqmate/', '');

            let blockAttributes = attributes;

            faqmateCopyPasteStyles[`${blockName}-styles`] = {};
            faqmateCopyPasteStyles[`global-style`] = {};

            if (blockAttributes && faqmateCopyPasteStyles) {
                Object.keys(blockAttributes).map(attribute => {
                    let key = attribute;

                    if (
                        undefined !== attributes[attribute] &&
                        null !== attributes[attribute] &&
                        (key.includes('faqmate_') || key.includes('aff_')) &&
                        !excludeKeys.includes(key)
                    ) {
                        styles[key] = attributes[attribute];
                        parentStyle[attribute] = attributes[attribute];
                    }

                    return attribute;
                });
            }
            if (innerBlocks) {
                parentStyle.innerblocks = innerBlocks;
            }

            styles.stylesSavedTimeStamp = Date.now();

            faqmateCopyPasteStyles[`${blockName}-styles`] = parentStyle;
            faqmateCopyPasteStyles[`global-style`] = styles;

            faqmateCopyPasteStylesSetter.setItem('faqmateCopyPasteStyles', JSON.stringify(faqmateCopyPasteStyles));
        }
    };

    const pasteBlockStyles = blockData => {
        const { name, clientId, innerBlocks } = blockData;

        let styles;
        let pasteStyle;
        const parentAttr = {};

        const faqmateCopyPasteStyles = getFaqmateEditorLocalStorage('faqmateCopyPasteStyles');

        if (name.includes('faqmate/')) {
            styles = faqmateCopyPasteStyles[`global-style`];

            const blockName = name.replace('faqmate/', '');
            const blockAttributes = blockData.attributes;

            pasteStyle = faqmateCopyPasteStyles[`${blockName}-styles`];

            if (blockAttributes && pasteStyle) {
                updateBlockStyles(clientId, pasteStyle);

                if (innerBlocks) {
                    const childAttr = {};

                    innerBlocks.map((childBlock, index) => {
                        const childName = childBlock.name.replace('faqmate/', '');
                        const blockAttributes = childBlock.attributes;

                        if (pasteStyle.innerblocks[index].name === 'faqmate/' + childName) {
                            Object.keys(blockAttributes).map(attribute => {
                                if (attribute.includes('faqmate_') && !excludeKeys.includes(attribute)) {
                                    childAttr[attribute] = pasteStyle.innerblocks[index].attributes[attribute];
                                }

                                return childAttr;
                            });
                        }
                        updateBlockStyles(childBlock.clientId, childAttr);

                        return childBlock;
                    });
                }
            } else if (blockAttributes && styles) {
                Object.keys(blockAttributes).map(attribute => {
                    const key = attribute;

                    Object.keys(styles).map(item => {
                        if (item === key && key.includes('faqmate_') && !excludeKeys.includes(key)) {
                            parentAttr[attribute] = styles[key];
                        }

                        return parentAttr;
                    });

                    return parentAttr;
                });

                updateBlockStyles(clientId, parentAttr);
            }
        }
    };

    const updateBlockStyles = (clientId, styles) => {
        dispatch('core/block-editor').updateBlockAttributes(clientId, styles);
    };

    useShortcut('faqmate/copy', event => {
        copyStylesHandler();
        event.preventDefault();
    });
    useShortcut('faqmate/paste', event => {
        pasteStylesHandler();
        event.preventDefault();
    });

    if (hasMultiSelection()) {
        stylesText = __('Styles', 'faqmate-blocks');
    }

    return (
        <>
            <PluginBlockSettingsMenuItem
                icon={SettingsIcons.copy}
                label={__('Faqmate Copy Styles', 'faqmate-blocks')}
                onClick={copyStylesHandler}
            />
            <PluginBlockSettingsMenuItem
                icon={SettingsIcons.paste}
                label={__('Faqmate Paste Styles', 'faqmate-blocks')}
                onClick={pasteStylesHandler}
            />
        </>
    );
};

registerPlugin('faqmate-block-copy-paste', {
    render: faqBCopyPasteStyles
});
